export {
  getDuration,
  getDate,
  getTime,
  formatDateRange,
  getTranslatedDay,
  getTranslatedMonth,
} from "./dateUtils";

export {
  DEFAULT_BUTTON_COLOR,
  parseHexColor,
  pickReadableTextColor,
  resolveButtonBackground,
  buttonInlineStyle,
  buildEmailButton,
} from "./colorUtils";

export const deepTransform = (data, fn) => {
  if (Array.isArray(data)) {
    return data.map((item) => deepTransform(item, fn));
  }
  if (data != null && typeof data === "object" && !(data instanceof Date)) {
    const keys = Object.keys(data);
    return keys.reduce((acc, key) => {
      acc[key] = deepTransform(data[key], fn);
      return acc;
    }, {});
  }
  return fn(data);
};

export const sanitize = (src) => {
  if (src instanceof Date) {
    return src;
  }

  if (typeof src !== "string") {
    return src;
  }

  // Trims the whitespace from the string
  const trimmed = src.trim();

  if (trimmed === "") {
    return null;
  }

  return trimmed;
};

export const deepSanitize = (data) => deepTransform(data, sanitize);

// Fields that contain structured data (like TipTap JSON) and should not be sanitized
export const STRUCTURED_DATA_FIELDS = [
  "descriptionRichI18n",
  "customText",
  "content",
  "contentHtml",
];

/**
 * Deep sanitize data while excluding specified fields from sanitization.
 * Useful for fields that contain structured data (like TipTap JSON)
 * that should preserve whitespace.
 *
 * @param {*} data - The data to sanitize
 * @param {string[]} excludeFields - Array of field names to exclude
 * @returns {*} The sanitized data with excluded fields preserved
 */
export const deepSanitizeWithExclusions = (
  data,
  excludeFields = STRUCTURED_DATA_FIELDS
) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return deepSanitize(data);
  }

  const excluded = {};
  const toSanitize = {};

  Object.keys(data).forEach((key) => {
    if (excludeFields.includes(key)) {
      excluded[key] = data[key]; // Keep original reference, no sanitization
    } else {
      toSanitize[key] = data[key];
    }
  });

  return { ...deepSanitize(toSanitize), ...excluded };
};

/**
 * Returns true when an HTML string has no visible content. Strips all tags and
 * surrounding whitespace, so an empty rich-text doc (e.g. Tiptap's "<p></p>")
 * counts as empty. Shared between web (form validation) and api (send-time
 * guard) so both ends agree on what "empty content" means.
 *
 * @param {string} html - Serialized HTML body
 * @returns {boolean}
 */
export const isContentEmpty = (html) => {
  if (!html) return true;
  return html.replace(/<[^>]*>/g, "").trim().length === 0;
};

/**
 * Returns true when a rich-text body has no visible content, whether it is
 * serialized HTML or a Tiptap JSON document. An empty Tiptap doc still carries
 * an outer `{"type":"doc"}` wrapper, so a plain string check on it would report
 * a blank policy as non-empty.
 *
 * Shared so that the compliance editor, the API's gate-active rule and the
 * refusal to turn the compliance flag on all agree on what "empty" means.
 *
 * @param {string|object|null|undefined} content - HTML string or Tiptap JSON
 * @returns {boolean}
 */
export const isRichTextEmpty = (content) => {
  if (!content) return true;

  let doc = content;

  if (typeof content === "string") {
    const trimmed = content.trim();
    if (trimmed === "") return true;

    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
      return isContentEmpty(trimmed);
    }

    try {
      doc = JSON.parse(trimmed);
    } catch {
      return isContentEmpty(trimmed);
    }
  }

  if (typeof doc !== "object") return true;

  // A node counts as content if it carries text, or if it is a leaf that is
  // visible on its own (an image, a horizontal rule).
  const hasContent = (node) => {
    if (Array.isArray(node)) return node.some(hasContent);
    if (!node || typeof node !== "object") return false;
    if (typeof node.text === "string" && node.text.trim() !== "") return true;
    if (node.type === "image" || node.type === "horizontalRule") return true;
    return hasContent(node.content);
  };

  return !hasContent(doc);
};
